import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/LanguageContext";
import ProfileBody from "@/components/vangard/ProfileBody";
import AccountShell from "@/components/vangard/AccountShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCIALS = ["website", "instagram", "linkedin", "twitter", "youtube", "github", "facebook", "tiktok", "spotify"];
const INDUSTRIES = [
  "Technology", "Finance", "Real Estate", "Retail", "Hospitality",
  "Manufacturing", "Services", "Media", "Health", "Energy", "Other",
];
const PREMIUM_TIERS = new Set(["gold", "vip", "corporate"]);

function isValidUrl(v) {
  if (!v) return true;
  try { const u = new URL(v); return u.protocol === "http:" || u.protocol === "https:"; }
  catch { return false; }
}

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-white" : "bg-white/[0.12]"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full transition-transform ${on ? "bg-black translate-x-6" : "bg-white translate-x-1"}`}
      />
    </button>
  );
}

export default function AccountProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", bio: "", picture: "",
    headline: "", company: "", role: "", industry: "",
    location: "", website: "", expertise: [],
    offering: "", looking_for: "", is_public: false,
    social_links: {},
  });
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setMe(data);
        setForm({
          name: data.name || "",
          bio: data.bio || "",
          picture: data.picture || "",
          headline: data.headline || "",
          company: data.company || "",
          role: data.role || "",
          industry: data.industry || "",
          location: data.location || "",
          website: data.website || "",
          expertise: data.expertise || [],
          offering: data.offering || "",
          looking_for: data.looking_for || "",
          is_public: !!data.is_public,
          social_links: data.social_links || {},
        });
      } catch {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = () => {
    const v = tagInput.trim();
    if (!v || form.expertise.includes(v) || form.expertise.length >= 24) return;
    setField("expertise", [...form.expertise, v]);
    setTagInput("");
  };
  const removeTag = (tag) => setField("expertise", form.expertise.filter((x) => x !== tag));

  const save = async () => {
    for (const [k, v] of Object.entries(form.social_links)) {
      if (v && !isValidUrl(v)) { toast.error(`${k}: ${t("vc.account.bad-url")}`); return; }
    }
    if (form.website && !isValidUrl(form.website.startsWith("http") ? form.website : `https://${form.website}`)) {
      toast.error(t("vc.account.bad-url")); return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/auth/me`, form, { withCredentials: true });
      setMe(data);
      toast.success(t("vc.account.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("vc.account.save-fail"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  const tier = (me?.membership_tier || "silver").toLowerCase();
  const isPremium = PREMIUM_TIERS.has(tier);
  const slug = me?.slug;

  return (
    <AccountProfileView
      me={me}
      form={form}
      setField={setField}
      addTag={addTag}
      removeTag={removeTag}
      tagInput={tagInput}
      setTagInput={setTagInput}
      save={save}
      saving={saving}
      isPremium={isPremium}
      tier={tier}
      slug={slug}
      navigate={navigate}
      t={t}
    />
  );
}

function AccountProfileView(p) {
  const { t } = p;
  return (
    <AccountShell active="/account/profile" title={t("vc.account.title")}>
      <div data-testid="account-profile-page">
        <ProfileBody {...p} />
      </div>
    </AccountShell>
  );
}
